import { useLocation } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import { ChartContext } from "../Context/ChartContext/ChartContext.jsx";
import { useHistory } from "react-router-dom";

import MUIBox from "@mui/material/Box";
import MUIButton from "@mui/material/Button";
import MUIStack from "@mui/material/Stack";
import MUIArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { alpha } from "@mui/material/styles";
import LayerFunctional from "../LayerFunctional";
import CardChartHeader from "../Component/Card/Chart/CardChartHeader";
import CardChartDemographics from "../Component/Card/Chart/CardChartDemographics";
import CardChartExternalIds from "../Component/Card/Chart/CardChartExternalIds";
import CardChartContact from "../Component/Card/Chart/CardChartContact";
import CardChartNotes from "../Component/Card/Chart/CardChartNotes";
import CardChartPatientEquipment from "../Component/Card/Chart/CardChartPatientEquipment";
import CardChartOrderEquipment from "../Component/Card/Chart/CardChartOrderEquipment";
import CardChartRouting from "../Component/Card/Chart/CardChartRouting";
import CardChartClinical from "../Component/Card/Chart/CardChartClinical";
import CardChartAttachments from "../Component/Card/Chart/CardChartAttachments";
import CardChartSales from "../Component/Card/Chart/CardChartSales";
import CardChartInsuranceIQ from "../Component/Card/Chart/CardChartInsuranceIQ";
import CardChartInsurance from "../Component/Card/Chart/CardChartInsurance";
import CardChartViolations from "../Component/Card/Chart/CardChartViolations";
import CardChartCommunications from "../Component/Card/Chart/CardChartCommunications";
import CardChartFeedback from "../Component/Card/Chart/CardChartFeedback";
import CardChartCreateTicketNote from "../Component/Card/Chart/CardChartCreateTicketNote";
import CardChartApproval from "../Component/Card/Chart/CardChartApproval";
import CardChartFinancial from "../Component/Card/Chart/CardChartFinancial";
import CardChartDelivery from "../Component/Card/Chart/CardChartDelivery";
import CardChartCreateOrder from "../Component/Card/Chart/CardChartCreateOrder";
import CardChartMeasurementInSitting from "../Component/Card/Chart/CardChartMeasurementInSitting";
import CardChartAssessment from "../Component/Card/Chart/CardChartAssessment.jsx";
import CardChartAtpAssessment from "../Component/Card/Chart/CardChartATPAssessment.jsx";
import CardChartServiceParts from "../Component/Card/Chart/CardChartServiceParts";
import CardChartIntakeQuestionnaire from "../Component/Card/Chart/CardChartIntakeQuestionnaire.jsx";
import CardChartInsuranceDiscovery from "../Component/Card/Chart/CardChartInsuranceDiscovery.jsx";
import CardChartSignature from "../Component/Card/Chart/CardChartSignature.jsx";
import CardChartPreview from "../Component/Card/Chart/CardChartPreview.jsx";
import CardChartHomeAssessment from "../Component/Card/Chart/CardChartHomeAssessment.jsx";
import CardChartPartReturn from "../Component/Card/Chart/CardChartPartReturn.jsx";
import MainContext from "../Context/MainContext/MainContext.jsx";
import User from "../Utility/Crud/User";
import Security from "../Utility/Security";
import CardChartFrequency from "../Component/Card/Chart/CardChartFrequency.jsx";
import Progress from "../Component/Progress";
import DialogManageSignature from "../Component/Dialog/DialogManageSignature.jsx";
import ChartNavigationBar from "../Component/ChartNavigationBar.jsx";
import HorizontalStepper from "../Component/HorizontalStepper.jsx";
import api from "../Seating/Security/api.jsx";

import InsuranceDiscovery from "../Utility/InsuranceDiscovery.jsx";

/**
 * ChartPatient component displays patient chart data.
 * Fetches chart data based on the current location and renders it.
 * @returns {JSX.Element}
 */
export default function Chart() {
  const mainContext = useContext(MainContext);
  const chartContext = useContext(ChartContext);
  const location = useLocation();
  const history = useHistory();

  const [rightColumnCardsFromChartView, setRightColumnCardsFromChartView] =
    useState([]);
  const [flashCardAnchorKey, setFlashCardAnchorKey] = useState(null);

  const cardAnchorRefs = useRef({});
  const leftColumnScrollContainerRef = useRef(null);
  const rightColumnScrollContainerRef = useRef(null);
  const flashTimeoutRef = useRef(null);

  /**
   * Creates a stable DOM anchor key for any rendered chart card.
   * @param {string} columnName - The column that contains the card.
   * @param {string} cardName - The chart card component name.
   * @param {number} cardIndex - The card position within its column.
   * @returns {string}
   */
  const getCardAnchorKey = (columnName, cardName, cardIndex) =>
    `${columnName}-${cardName}-${cardIndex}`;

  /**
   * Scrolls to a chart card and briefly flashes it so the target is obvious.
   * @param {string} cardAnchorKey - The unique anchor key for the target card.
   */
  const scrollToCard = (cardAnchorKey) => {
    if (!cardAnchorRefs.current[cardAnchorKey]) {
      return;
    }

    if (flashTimeoutRef.current) {
      window.clearTimeout(flashTimeoutRef.current);
    }

    setFlashCardAnchorKey(cardAnchorKey);
    flashTimeoutRef.current = window.setTimeout(() => {
      setFlashCardAnchorKey(null);
      flashTimeoutRef.current = null;
    }, 900);

    cardAnchorRefs.current[cardAnchorKey].scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  };

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) {
        window.clearTimeout(flashTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const requestedScrollToCard = chartContext.requestedScrollToCard;
    if (!requestedScrollToCard?.cardName) {
      return;
    }

    const { cardName, columnName } = requestedScrollToCard;
    const allCardAnchorKeys = Object.keys(cardAnchorRefs.current);
    const preferredCardAnchorKey = columnName
      ? allCardAnchorKeys.find((cardAnchorKey) =>
          cardAnchorKey.startsWith(`${columnName}-${cardName}-`),
        )
      : null;
    const fallbackCardAnchorKey = allCardAnchorKeys.find((cardAnchorKey) =>
      cardAnchorKey.includes(`-${cardName}-`),
    );
    const nextCardAnchorKey = preferredCardAnchorKey || fallbackCardAnchorKey;

    if (nextCardAnchorKey) {
      scrollToCard(nextCardAnchorKey);
    }

    chartContext.clearRequestedScrollToCard?.();
  }, [chartContext.requestedScrollToCard]);

  /**
   * Get a list of all available chart views given the current data. This is
   * set up like a database row because we will likely move this to a
   * chartView table in the future. The default view is set to id 0 since that
   * is always dynamically injected.
   */
  const getChartViews = () => {
    let chartViews = {};

    if (
      Security.hasPermission("chart.allChartViews") ||
      User.hasTeam(
        mainContext.teamsIndexed[17],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) // Patient Financial Services
    ) {
      chartViews[1] = {
        id: 1,
        name: "Patient Financial Services",
        view: patientFinancialServicesChartView,
      };
    }

    if (
      Security.hasPermission("chart.allChartViews") ||
      User.hasTeam(
        mainContext.teamsIndexed[35],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) || // Sales
      User.hasTeam(
        mainContext.teamsIndexed[23],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) || // Marketing
      User.hasTeam(
        mainContext.teamsIndexed[24],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) // Campaign
    ) {
      chartViews[2] = {
        id: 2,
        name: "Sales",
        view: salesChartView,
      };
    }

    if (
      Security.hasPermission("chart.allChartViews") ||
      User.hasTeam(
        mainContext.teamsIndexed[30],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) || // Business Support
      User.hasTeam(
        mainContext.teamsIndexed[33],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) // Business Support
    ) {
      chartViews[3] = {
        id: 3,
        name: "Business Support",
        view: businessSupportChartView,
      };
    }

    if (
      Security.hasPermission("chart.allChartViews") ||
      User.hasTeam(
        mainContext.teamsIndexed[5],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) || // Customer Service Representatives
      User.hasTeam(
        mainContext.teamsIndexed[6],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) || // Customer Experience
      User.hasTeam(
        mainContext.teamsIndexed[7],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) || // Customer Service Technicians
      User.hasTeam(
        mainContext.teamsIndexed[9],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) // Care Coordination
    ) {
      chartViews[4] = {
        id: 4,
        name: "Service",
        view: serviceChartView,
      };
    }

    if (
      User.hasTeam(
        mainContext.teamsIndexed[1],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) // Software Development
    ) {
      chartViews[5] = {
        id: 5,
        name: "Admin",
        view: adminChartView,
      };
    }

    if (
      Security.hasPermission("chart.allChartViews") ||
      User.hasTeam(
        mainContext.teamsIndexed[14],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) || // Initial Billing
      User.hasTeam(
        mainContext.teamsIndexed[42],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) || // Resupply
      User.hasTeam(
        mainContext.teamsIndexed[44],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) || // Insurance Collections
      User.hasTeam(
        mainContext.teamsIndexed[46],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) || // Insurance Collections
      User.hasTeam(
        mainContext.teamsIndexed[48],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) // Insurance Collections
    ) {
      chartViews[6] = {
        id: 6,
        name: "Billing",
        view: billingChartView,
      };
    }

    if (
      Security.hasPermission("chart.allChartViews") ||
      User.hasTeam(
        mainContext.teamsIndexed[8],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) // Approvals
    ) {
      chartViews[7] = {
        id: 7,
        name: "Approval",
        view: approvalChartView,
      };
    }

    if (
      Security.hasPermission("chart.allChartViews") ||
      User.hasTeam(
        mainContext.teamsIndexed[10],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) || // Preauth Submission
      User.hasTeam(
        mainContext.teamsIndexed[39],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) // Followup
    ) {
      chartViews[8] = {
        id: 8,
        name: "Insurance",
        view: insuranceChartView,
      };
    }

    if (
      Security.hasPermission("chart.allChartViews") ||
      User.hasTeam(
        mainContext.teamsIndexed[29],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) // Review
    ) {
      chartViews[9] = {
        id: 9,
        name: "Review",
        view: reviewChartView,
      };
    }

    if (
      Security.hasPermission("chart.allChartViews") ||
      User.hasTeam(
        mainContext.teamsIndexed[12],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) || // Fulfillment
      User.hasTeam(
        mainContext.teamsIndexed[13],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) // Purchasing
    ) {
      chartViews[10] = {
        id: 10,
        name: "Purchasing",
        view: purchasingChartView,
      };
    }

    if (
      Security.hasPermission("chart.allChartViews") ||
      User.hasTeam(
        mainContext.teamsIndexed[34],
        mainContext.currentUser,
        mainContext.teamUsersIndexed,
      ) // Field Operations
    ) {
      chartViews[11] = {
        id: 11,
        name: "Field Operations",
        view: fieldOperationsChartView,
      };
    }

    // Ensure every user has at least one available chart view.
    if (Object.keys(chartViews).length === 0) {
      chartViews[12] = {
        id: 12,
        name: "Default",
        view: defaultChartView,
      };
    }

    return chartViews;
  };

  useEffect(() => {
    chartContext.setChartViews(getChartViews());
  }, []);

  /**
   * Set the right column cards when the selected view or initial chart data changes.
   */
  useEffect(() => {
    const chartViews = getChartViews();
    const orderType =
      chartContext?.originalChart?.orderActivity?.[
        chartContext.orderId
      ]?.orderType?.toLowerCase();

    // Sets the selected chart view to the one set in context, else falls
    // back to the admin view if available, else falls back to the first
    // view in the list.
    const selectedChartViewId = chartViews[chartContext.selectedChartViewId]
      ? chartContext.selectedChartViewId
      : chartViews[5]?.id || Object.values(chartViews)[0]?.id;
    if (
      selectedChartViewId !== chartContext.selectedChartViewId &&
      selectedChartViewId !== undefined
    ) {
      chartContext.setSelectedChartViewId(selectedChartViewId);
    }
    const selectedView = chartViews[selectedChartViewId]?.view;
    const orderTypeCards = orderType ? selectedView?.[orderType] : undefined;
    const defaultCards = selectedView?.default;
    const rightColumnCardsFromSelectedChartView =
      orderTypeCards || defaultCards;

    if (!rightColumnCardsFromSelectedChartView) {
      console.error("Chart view is missing a default card configuration.", {
        selectedChartViewId,
        orderType,
      });
      return;
    }

    setRightColumnCardsFromChartView(rightColumnCardsFromSelectedChartView);
  }, [
    chartContext.selectedChartViewId,
    chartContext.originalChart,
    chartContext.orderId,
  ]);

  const cardComponentMap = {
    CardChartRouting: CardChartRouting,
    CardChartDemographics: CardChartDemographics,
    CardChartContact: CardChartContact,
    CardChartInsuranceIQ: CardChartInsuranceIQ,
    CardChartInsurance: CardChartInsurance,
    CardChartClinical: CardChartClinical,
    CardChartDelivery: CardChartDelivery,
    CardChartMeasurementInSitting: CardChartMeasurementInSitting,
    CardChartOrderEquipment: CardChartOrderEquipment,
    CardChartApproval: CardChartApproval,
    CardChartFinancial: CardChartFinancial,
    CardChartAttachments: CardChartAttachments,
    CardChartSales: CardChartSales,
    CardChartFeedback: CardChartFeedback,
    CardChartCommunications: CardChartCommunications,
    CardChartExternalIds: CardChartExternalIds,
    CardChartNotes: CardChartNotes,
    CardChartPatientEquipment: CardChartPatientEquipment,
    CardChartViolations: CardChartViolations,
    CardChartCreateTicketNote: CardChartCreateTicketNote,
    CardChartCreateOrder: CardChartCreateOrder,
    CardChartAssessment: CardChartAssessment,
    CardChartAtpAssessment: CardChartAtpAssessment,
    CardChartServiceParts: CardChartServiceParts,
    CardChartFrequency: CardChartFrequency,
    CardChartIntakeQuestionnaire: CardChartIntakeQuestionnaire,
    CardChartSignature: CardChartSignature,
    CardChartPreview: CardChartPreview,
    CardChartHomeAssessment: CardChartHomeAssessment,
    CardChartInsuranceDiscovery: CardChartInsuranceDiscovery,
    CardChartPartReturn: CardChartPartReturn,
  };

  const defaultChartView = {
    new: [
      { name: "CardChartRouting" },
      { name: "CardChartInsurance" },
      {
        name: "CardChartApproval",
        settings: {
          allowApproval: false,
          allowApprovalOnNoApprovedProducts: false,
        },
      },
      { name: "CardChartClinical" },
      {
        name: "CardChartOrderEquipment",
        settings: {
          showGenerateDswoButton: false,
          allowToggleAbn: false,
          allowApproving: false,
        },
      },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    "accessory after sale": [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showInsuranceDiscoveryButton: false },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartClinical" },
      {
        name: "CardChartServiceParts",
        settings: {
          showWarrantyColumn: false,
          showThirtyDayFitColumn: false,
          showEstimatedLabor: false,
        },
      },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    service: [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartClinical" },
      { name: "CardChartServiceParts" },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    "billing issue": [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
        },
      },
      { name: "CardChartClinical" },
      {
        name: "CardChartOrderEquipment",
        settings: {
          showGenerateDswoButton: false,
          allowToggleAbn: false,
          allowApproving: false,
        },
      },
      { name: "CardChartServiceParts" },
      { name: "CardChartAttachments" },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    pickup: [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          allowApprovalOnNoApprovedProducts: false,
          showBillingLocation: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showAuthNumber: false,
          showPredeterminationNumber: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartClinical" },
      { name: "CardChartAttachments" },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    "re-supply": [
      { name: "CardChartRouting" },
      { name: "CardChartInsurance" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          allowApprovalOnNoApprovedProducts: false,
        },
      },
      { name: "CardChartClinical" },
      {
        name: "CardChartOrderEquipment",
        settings: {
          showGenerateDswoButton: false,
          allowToggleAbn: false,
          allowApproving: false,
        },
      },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartFrequency" },
      { name: "CardChartExternalIds" },
    ],
    default: [{ name: "CardChartRouting" }],
  };

  const salesChartView = {
    default: [
      { name: "CardChartRouting" },
      { name: "CardChartSales" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { allowCreateNoteAlways: false },
      },
      { name: "CardChartClinical" },
      {
        name: "CardChartOrderEquipment",
        settings: {
          allowToggleAbn: false,
          allowApproving: false,
          showRentalColumn: false,
          showApprovedColumn: false,
          showAbnColumn: false,
          showDeliveredColumn: false,
          showBrightreeColumn: false,
        },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartDelivery" },
      { name: "CardChartAssessment", settings: { startInEditMode: false } },
      { name: "CardChartIntakeQuestionnaire" },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
  };

  const fieldOperationsChartView = {
    new: [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartInsurance" },
      {
        name: "CardChartApproval",
        settings: {
          allowApproval: false,
          allowApprovalOnNoApprovedProducts: false,
        },
      },
      { name: "CardChartFinancial" },
      {
        name: "CardChartOrderEquipment",
        settings: {
          allowToggleAbn: false,
          allowApproving: false,
          showBrightreeColumn: false,
          showRentalColumn: false,
          showAbnColumn: false,
          showDeliveredColumn: false,
        },
      },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
      { name: "CardChartClinical" },
      { name: "CardChartAttachments" },
      { name: "CardChartExternalIds" },
    ],
    "accessory after sale": [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showInsuranceDiscoveryButton: false },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartFinancial" },
      {
        name: "CardChartServiceParts",
        settings: {
          showWarrantyColumn: false,
          showThirtyDayFitColumn: false,
          showEstimatedLabor: false,
        },
      },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
      { name: "CardChartClinical" },
      { name: "CardChartAttachments" },
      { name: "CardChartExternalIds" },
    ],
    service: [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartFinancial" },
      {
        name: "CardChartServiceParts",
        settings: {
          allowToggleAbn: false,
          allowApproving: false,
          showRentalColumn: false,
          showApprovedColumn: false,
          showAbnColumn: false,
          showDeliveredColumn: false,
        },
      },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
      { name: "CardChartClinical" },
      { name: "CardChartAttachments" },
      { name: "CardChartExternalIds" },
    ],
    "billing issue": [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartClinical" },
      {
        name: "CardChartOrderEquipment",
        settings: {
          allowToggleAbn: false,
          allowApproving: false,
          showBrightreeColumn: false,
          showRentalColumn: false,
          showAbnColumn: false,
          showDeliveredColumn: false,
        },
      },
      { name: "CardChartServiceParts" },
      { name: "CardChartAttachments" },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    pickup: [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          allowApprovalOnNoApprovedProducts: false,
          showBillingLocation: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showAuthNumber: false,
          showPredeterminationNumber: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartFinancial" },
      {
        name: "CardChartOrderEquipment",
        settings: {
          allowAdding: false,
          allowToggleAbn: false,
          allowApproving: false,
          showBrightreeColumn: false,
          showRentalColumn: false,
          showApprovedColumn: false,
          showAbnColumn: false,
          showDeliveredColumn: false,
        },
      },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
      { name: "CardChartClinical" },
      { name: "CardChartAttachments" },
      { name: "CardChartExternalIds" },
    ],
    "re-supply": [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartInsurance" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          allowApprovalOnNoApprovedProducts: false,
        },
      },
      { name: "CardChartFinancial" },
      {
        name: "CardChartOrderEquipment",
        settings: {
          allowAdding: false,
          allowToggleAbn: false,
          allowApproving: false,
          showBrightreeColumn: false,
          showRentalColumn: false,
          showApprovedColumn: false,
          showAbnColumn: false,
          showDeliveredColumn: false,
        },
      },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
      { name: "CardChartClinical" },
      { name: "CardChartAttachments" },
      { name: "CardChartFrequency" },
      { name: "CardChartExternalIds" },
    ],
    default: [{ name: "CardChartRouting" }],
  };

  const businessSupportChartView = {
    new: [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartDemographics" },
      { name: "CardChartSales" },
      { name: "CardChartInsurance" },
      { name: "CardChartClinical" },
      { name: "CardChartOrderEquipment" },
      {
        name: "CardChartApproval",
        settings: { allowApprovalOnNoApprovedProducts: false },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "accessory after sale": [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartDemographics" },
      { name: "CardChartSales" },
      { name: "CardChartInsurance" },
      { name: "CardChartClinical" },
      {
        name: "CardChartServiceParts",
        settings: {
          showWarrantyColumn: false,
          showThirtyDayFitColumn: false,
          showEstimatedLabor: false,
        },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    pickup: [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartDemographics" },
      { name: "CardChartSales" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartClinical" },
      { name: "CardChartOrderEquipment", settings: { allowAdding: false } },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApprovalOnNoApprovedProducts: false,
          showBillingLocation: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showAuthNumber: false,
          showPredeterminationNumber: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "billing issue": [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartDemographics" },
      { name: "CardChartSales" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartClinical" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartServiceParts" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
        },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    service: [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartDemographics" },
      { name: "CardChartSales" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartClinical" },
      { name: "CardChartServiceParts" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "re-supply": [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
      { name: "CardChartClinical" },
      { name: "CardChartAttachments" },
      { name: "CardChartFrequency" },
      { name: "CardChartExternalIds" },
    ],
    default: [{ name: "CardChartRouting" }],
  };

  const serviceChartView = {
    new: [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartClinical" },
      { name: "CardChartCommunications" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartAttachments" },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
    ],
    "accessory after sale": [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: {
          showCheckSameSimButton: false,
          showCheckSNFButton: false,
          showInsuranceDiscoveryButton: false,
        },
      },
      { name: "CardChartClinical" },
      { name: "CardChartCommunications" },
      {
        name: "CardChartServiceParts",
        settings: {
          showWarrantyColumn: false,
          showThirtyDayFitColumn: false,
          showEstimatedLabor: false,
        },
      },
      { name: "CardChartAttachments" },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
    ],
    service: [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartClinical" },
      { name: "CardChartCommunications" },
      { name: "CardChartServiceParts" },
      { name: "CardChartAttachments" },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
    ],
    "billing issue": [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartClinical" },
      { name: "CardChartCommunications" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartAttachments" },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
    ],
    pickup: [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartClinical" },
      { name: "CardChartCommunications" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartAttachments" },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
    ],
    "re-supply": [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartClinical" },
      { name: "CardChartCommunications" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartAttachments" },
      { name: "CardChartSales" },
      { name: "CardChartDelivery" },
    ],
    default: [{ name: "CardChartRouting" }],
  };

  const insuranceChartView = {
    new: [
      { name: "CardChartRouting" },
      { name: "CardChartInsurance" },
      { name: "CardChartSales" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartClinical" },
      { name: "CardChartFinancial" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartAttachments" },
      {
        name: "CardChartApproval",
        settings: {
          allowApproval: false,
          allowApprovalOnNoApprovedProducts: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "accessory after sale": [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showInsuranceDiscoveryButton: false },
      },
      { name: "CardChartSales" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartClinical" },
      { name: "CardChartFinancial" },
      {
        name: "CardChartServiceParts",
        settings: {
          showWarrantyColumn: false,
          showThirtyDayFitColumn: false,
          showEstimatedLabor: false,
        },
      },
      { name: "CardChartAttachments" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    service: [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartSales" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartClinical" },
      { name: "CardChartFinancial" },
      { name: "CardChartServiceParts" },
      { name: "CardChartAttachments" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "billing issue": [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartSales" },
      { name: "CardChartDemographics" },
      { name: "CardChartClinical" },
      { name: "CardChartFinancial" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartServiceParts" },
      { name: "CardChartAttachments" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    pickup: [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      { name: "CardChartFinancial" },
      { name: "CardChartOrderEquipment", settings: { allowAdding: false } },
      { name: "CardChartAttachments" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          allowApprovalOnNoApprovedProducts: false,
          showBillingLocation: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showAuthNumber: false,
          showPredeterminationNumber: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "re-supply": [
      { name: "CardChartRouting" },
      { name: "CardChartInsurance" },
      { name: "CardChartSales" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartClinical" },
      { name: "CardChartFinancial" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartAttachments" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApproval: false,
          allowApprovalOnNoApprovedProducts: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartFrequency" },
      { name: "CardChartExternalIds" },
    ],
    default: [{ name: "CardChartRouting" }],
  };

  const approvalChartView = {
    new: [
      { name: "CardChartRouting" },
      { name: "CardChartInsuranceIQ" },
      { name: "CardChartInsurance" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartAttachments" },
      { name: "CardChartClinical" },
      { name: "CardChartFinancial" },
      {
        name: "CardChartApproval",
        settings: { allowApprovalOnNoApprovedProducts: false },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    "accessory after sale": [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showInsuranceDiscoveryButton: false },
      },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartServiceParts",
        settings: {
          showWarrantyColumn: false,
          showThirtyDayFitColumn: false,
          showEstimatedLabor: false,
        },
      },
      { name: "CardChartAttachments" },
      { name: "CardChartClinical" },
      { name: "CardChartFinancial" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    service: [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartServiceParts" },
      { name: "CardChartAttachments" },
      { name: "CardChartClinical" },
      { name: "CardChartFinancial" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    "billing issue": [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartAttachments" },
      { name: "CardChartClinical" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartServiceParts" },
      { name: "CardChartFinancial" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    pickup: [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartOrderEquipment", settings: { allowAdding: false } },
      { name: "CardChartAttachments" },
      { name: "CardChartClinical" },
      { name: "CardChartFinancial" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApprovalOnNoApprovedProducts: false,
          showBillingLocation: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showAuthNumber: false,
          showPredeterminationNumber: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    "re-supply": [
      { name: "CardChartRouting" },
      { name: "CardChartInsurance" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartAttachments" },
      { name: "CardChartClinical" },
      { name: "CardChartFinancial" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApprovalOnNoApprovedProducts: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartFrequency" },
      { name: "CardChartExternalIds" },
    ],
    default: [{ name: "CardChartRouting" }],
  };

  const reviewChartView = {
    new: [
      { name: "CardChartRouting" },
      { name: "CardChartInsurance" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      {
        name: "CardChartApproval",
        settings: { allowApprovalOnNoApprovedProducts: false },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "accessory after sale": [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showInsuranceDiscoveryButton: false },
      },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      {
        name: "CardChartServiceParts",
        settings: {
          showWarrantyColumn: false,
          showThirtyDayFitColumn: false,
          showEstimatedLabor: false,
        },
      },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    service: [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      { name: "CardChartServiceParts" },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "billing issue": [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartClinical" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartServiceParts" },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    pickup: [
      { name: "CardChartRouting" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      { name: "CardChartOrderEquipment", settings: { allowAdding: false } },
      { name: "CardChartAttachments" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApprovalOnNoApprovedProducts: false,
          showBillingLocation: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showAuthNumber: false,
          showPredeterminationNumber: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "re-supply": [
      { name: "CardChartRouting" },
      { name: "CardChartInsurance" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApprovalOnNoApprovedProducts: false,
        },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartFrequency" },
      { name: "CardChartExternalIds" },
    ],
    default: [{ name: "CardChartRouting" }],
  };

  const patientFinancialServicesChartView = {
    new: [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartInsurance" },
      {
        name: "CardChartApproval",
        settings: { allowApprovalOnNoApprovedProducts: false },
      },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    "accessory after sale": [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartInsurance" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      {
        name: "CardChartServiceParts",
        settings: {
          showWarrantyColumn: false,
          showThirtyDayFitColumn: false,
          showEstimatedLabor: false,
        },
      },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    service: [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartServiceParts" },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    "billing issue": [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
        },
      },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartServiceParts" },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    pickup: [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApprovalOnNoApprovedProducts: false,
          showBillingLocation: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showAuthNumber: false,
          showPredeterminationNumber: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartOrderEquipment", settings: { allowAdding: false } },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    "re-supply": [
      { name: "CardChartRouting" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartInsurance" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApprovalOnNoApprovedProducts: false,
        },
      },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartAttachments" },
      { name: "CardChartFinancial" },
      { name: "CardChartSales" },
      { name: "CardChartFrequency" },
      { name: "CardChartExternalIds" },
    ],
    default: [{ name: "CardChartRouting" }],
  };

  const billingChartView = {
    new: [
      { name: "CardChartRouting" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      { name: "CardChartInsurance" },
      {
        name: "CardChartOrderEquipment",
        settings: {
          showGenerateDswoButton: false,
          allowToggleAbn: false,
          allowApproving: false,
          showPointsColumn: false,
        },
      },
      { name: "CardChartApproval" },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "accessory after sale": [
      { name: "CardChartRouting" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      { name: "CardChartInsurance" },
      {
        name: "CardChartServiceParts",
        settings: {
          showWarrantyColumn: false,
          showThirtyDayFitColumn: false,
          showEstimatedLabor: false,
        },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    service: [
      { name: "CardChartRouting" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartServiceParts" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartFinancial" },
      {
        name: "CardChartAttachments",
        settings: { showAiQuoteParserCheckbox: false },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "billing issue": [
      { name: "CardChartRouting" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartClinical" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      {
        name: "CardChartOrderEquipment",
        settings: {
          showGenerateDswoButton: false,
          allowToggleAbn: false,
          allowApproving: false,
          showPointsColumn: false,
        },
      },
      { name: "CardChartServiceParts" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
        },
      },
      {
        name: "CardChartAttachments",
        settings: { showAiQuoteParserCheckbox: false },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    pickup: [
      { name: "CardChartRouting" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartClinical" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      {
        name: "CardChartAttachments",
        settings: { showAiQuoteParserCheckbox: false },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartSales" },
      { name: "CardChartExternalIds" },
    ],
    "re-supply": [
      { name: "CardChartRouting" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      { name: "CardChartInsurance" },
      {
        name: "CardChartOrderEquipment",
        settings: {
          showGenerateDswoButton: false,
          allowToggleAbn: false,
          allowApproving: false,
          showPointsColumn: false,
        },
      },
      { name: "CardChartApproval", settings: { showEvaluationType: false } },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartDelivery" },
      { name: "CardChartFrequency" },
      { name: "CardChartExternalIds" },
    ],
    default: [{ name: "CardChartRouting" }],
  };

  const purchasingChartView = {
    new: [
      { name: "CardChartRouting" },
      {
        name: "CardChartOrderEquipment",
        settings: {
          showGenerateDswoButton: false,
          allowToggleAbn: false,
          allowApproving: false,
          showPointsColumn: false,
        },
      },
      { name: "CardChartSales" },
      {
        name: "CardChartAttachments",
        settings: { showAiQuoteParserCheckbox: false },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "accessory after sale": [
      { name: "CardChartRouting" },
      {
        name: "CardChartServiceParts",
        settings: {
          showWarrantyColumn: false,
          showThirtyDayFitColumn: false,
          showEstimatedLabor: false,
        },
      },
      { name: "CardChartSales" },
      {
        name: "CardChartAttachments",
        settings: { showAiQuoteParserCheckbox: false },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    service: [
      { name: "CardChartRouting" },
      { name: "CardChartServiceParts" },
      { name: "CardChartSales" },
      {
        name: "CardChartAttachments",
        settings: { showAiQuoteParserCheckbox: false },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartPartReturn" },
      { name: "CardChartExternalIds" },
    ],
    "billing issue": [
      { name: "CardChartRouting" },
      {
        name: "CardChartOrderEquipment",
        settings: {
          showGenerateDswoButton: false,
          allowToggleAbn: false,
          allowApproving: false,
          showPointsColumn: false,
        },
      },
      { name: "CardChartServiceParts" },
      { name: "CardChartSales" },
      {
        name: "CardChartAttachments",
        settings: { showAiQuoteParserCheckbox: false },
      },
      { name: "CardChartDelivery" },
      { name: "CardChartPartReturn" },
      { name: "CardChartExternalIds" },
    ],
    default: [{ name: "CardChartRouting" }],
  };

  const adminChartView = {
    new: [
      { name: "CardChartRouting" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartInsuranceIQ" },
      { name: "CardChartInsurance" },
      { name: "CardChartSales" },
      { name: "CardChartAssessment", settings: { startInEditMode: false } },
      { name: "CardChartIntakeQuestionnaire" },
      { name: "CardChartClinical" },
      { name: "CardChartOrderEquipment" },
      {
        name: "CardChartApproval",
        settings: { allowApprovalOnNoApprovedProducts: false },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartCommunications" },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "accessory after sale": [
      { name: "CardChartRouting" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartInsurance" },
      { name: "CardChartSales" },
      { name: "CardChartAssessment", settings: { startInEditMode: false } },
      { name: "CardChartIntakeQuestionnaire" },
      { name: "CardChartClinical" },
      {
        name: "CardChartServiceParts",
        settings: {
          showWarrantyColumn: false,
          showThirtyDayFitColumn: false,
          showEstimatedLabor: false,
        },
      },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartCommunications" },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    service: [
      { name: "CardChartRouting" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartInsuranceIQ" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      { name: "CardChartServiceParts" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartCommunications" },
      { name: "CardChartDelivery" },
      { name: "CardChartPartReturn" },
      { name: "CardChartExternalIds" },
    ],
    "billing issue": [
      { name: "CardChartRouting" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartInsuranceIQ" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      { name: "CardChartOrderEquipment" },
      { name: "CardChartServiceParts" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
        },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartCommunications" },
      { name: "CardChartDelivery" },
      { name: "CardChartPartReturn" },
      { name: "CardChartExternalIds" },
    ],
    pickup: [
      { name: "CardChartRouting" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartInsuranceIQ" },
      {
        name: "CardChartInsurance",
        settings: { showCheckSameSimButton: false, showCheckSNFButton: false },
      },
      { name: "CardChartSales" },
      { name: "CardChartClinical" },
      { name: "CardChartOrderEquipment", settings: { allowAdding: false } },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApprovalOnNoApprovedProducts: false,
          showBillingLocation: false,
          showPlaceOfService: false,
          showCommissionTier: false,
          showAuthNumber: false,
          showPredeterminationNumber: false,
          showRentalOrSameSim: false,
          showFinancialHardship: false,
          showRentalOrPurchase: false,
        },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartCommunications" },
      { name: "CardChartDelivery" },
      { name: "CardChartExternalIds" },
    ],
    "re-supply": [
      { name: "CardChartRouting" },
      { name: "CardChartDemographics" },
      { name: "CardChartContact", settings: { shouldShowChangesAlert: false } },
      { name: "CardChartInsuranceIQ" },
      { name: "CardChartInsurance" },
      { name: "CardChartSales" },
      { name: "CardChartAssessment", settings: { startInEditMode: false } },
      { name: "CardChartClinical" },
      { name: "CardChartOrderEquipment" },
      {
        name: "CardChartApproval",
        settings: {
          showEvaluationType: false,
          allowApprovalOnNoApprovedProducts: false,
        },
      },
      { name: "CardChartFinancial" },
      { name: "CardChartAttachments" },
      { name: "CardChartCommunications" },
      { name: "CardChartDelivery" },
      { name: "CardChartFrequency" },
      { name: "CardChartExternalIds" },
    ],
    default: [{ name: "CardChartRouting" }],
  };

  /**
   * Renders the content.
   * @returns {JSX.Element} Grid layout with content display
   */
  const renderContent = () => {
    const isCreateTicketPageMode = [
      "pickup",
      "billingIssue",
      "service",
      "accessoryAfterSale",
    ].includes(chartContext.pageMode);
    const isWaitingForCreateTicket =
      isCreateTicketPageMode &&
      (chartContext.orderId === null || chartContext.orderId > 0);

    if (
      chartContext.isChartLoading ||
      !chartContext.originalChart ||
      !chartContext.chart ||
      !chartContext.patientId
    ) {
      return () => (
        <MUIBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <Progress message="Fetching data..." />
        </MUIBox>
      );
    }

    if (isWaitingForCreateTicket) {
      return () => (
        <MUIBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <Progress message="Preparing ticket..." />
        </MUIBox>
      );
    }

    // Determine mode
    let mode;
    switch (chartContext.pageMode) {
      case "pickup":
        mode = "createOrderActivityPickupTicket";
        break;
      case "billingIssue":
        mode = "createOrderActivityBillingIssueTicket";
        break;
      case "service":
        mode = "createOrderActivityServiceTicket";
        break;
      case "new":
        mode = "createOrderActivityNewOrder";
        break;
      case "accessoryAfterSale":
        mode = "createOrderActivityAccessoryAfterTheSale";
        break;
      case "resupply":
        mode = "createOrderActivityResupplyOrder";
        break;
      case "atpAssessment":
        mode = "createAtpAssessment";
      case "atpAssessmentEdit":
        mode = "editAtpAssessment";
        break;
      case "atpAssessmentSign":
        mode = "signAtpAssessment";
        break;
      case "atpAssessmentView":
        mode = "viewAtpAssessment";
        break;
      case "homeAssessment":
        mode = "createHomeAssessment";
        break;
      case "homeAssessmentEdit":
        mode = "editHomeAssessment";
        break;
      default:
        mode = chartContext.orderId ? "viewOrderActivity" : "viewPatient";
        break;
    }

    const hasViolations =
      Object.keys(chartContext.ruleViolations || {}).length > 0;

    // Define card components for each column based on mode
    let singleColumnCards = [];
    let leftColumnCards = [];
    let rightColumnCards = [];
    switch (mode) {
      case "createOrderActivityResupplyOrder":
      case "createOrderActivityNewOrder":
        singleColumnCards = [
          { name: "CardChartCreateOrder" },
          { name: "CardChartDemographics" },
          {
            name: "CardChartContact",
            settings: { shouldShowChangesAlert: false },
          },
        ];
        break;
      case "createOrderActivityAccessoryAfterTheSale":
        singleColumnCards = [
          { name: "CardChartCreateTicketNote" },
          { name: "CardChartDemographics" },
          {
            name: "CardChartContact",
            settings: { shouldShowChangesAlert: false },
          },
        ];
        break;
      case "createOrderActivityServiceTicket":
        singleColumnCards = [
          { name: "CardChartCreateTicketNote" },
          { name: "CardChartRouting", settings: { requiredValidation: true } },
          { name: "CardChartDemographics" },
          {
            name: "CardChartContact",
            settings: { shouldShowChangesAlert: false },
          },
          {
            name: "CardChartInsurance",
            settings: {
              showVerifiedChip: false,
              showCheckEligibilityButton: false,
              showEligibilitySummary: false,
              showCheckSameSimButton: false,
              showCheckSNFButton: false,
              showCreateVerificationNoteButton: false,
              allowDisablingFields: false,
            },
          },
        ];
        break;
      case "createOrderActivityBillingIssueTicket":
      case "createOrderActivityPickupTicket":
        singleColumnCards = [
          { name: "CardChartCreateTicketNote" },
          { name: "CardChartRouting", settings: { requiredValidation: true } },
          { name: "CardChartDemographics" },
          {
            name: "CardChartContact",
            settings: { shouldShowChangesAlert: false },
          },
        ];
        break;
      case "editAtpAssessment":
      case "createAtpAssessment":
        singleColumnCards = [
          {
            name: "CardChartDemographics",
            settings: { startInEditMode: true, showEditButton: false },
          },
          {
            name: "CardChartAtpAssessment",
            settings: { startInEditMode: true, showSignatureAlert: false },
          },
          {
            name: "CardChartMeasurementInSitting",
            settings: { startInEditMode: true },
          },
          {
            name: "CardChartHomeAssessment",
            settings: { startInEditMode: true },
          },
        ];
        break;
      case "signAtpAssessment":
        singleColumnCards = [
          {
            name: "CardChartPreview",
            settings: { type: "assistive-technology-report" },
          },
          {
            name: "CardChartSignature",
            settings: {
              text: "I have evaluated this patient and have determined that the listed equipment is medically necessary to meet these goals and to reach these outcomes. This evaluation and recommendation have been reviewed with the patient.",
            },
          },
        ];
        break;
      case "viewAtpAssessment":
        singleColumnCards = [
          {
            name: "CardChartPreview",
            settings: { type: "assistive-technology-report", isViewMode: true },
          },
        ];
        break;
      case "editHomeAssessment":
      case "createHomeAssessment":
        singleColumnCards = [
          {
            name: "CardChartHomeAssessment",
            settings: { startInEditMode: true },
          },
        ];
        break;
      case "viewOrderActivity":
        leftColumnCards = [{ name: "CardChartNotes" }];
        rightColumnCards = [
          ...(hasViolations ? [{ name: "CardChartViolations" }] : []),
          ...rightColumnCardsFromChartView,
        ];
        break;
      case "viewPatient":
        leftColumnCards = [
          { name: "CardChartDemographics", settings: { sidebar: true } },
          {
            name: "CardChartContact",
            settings: { sidebar: true, shouldShowChangesAlert: false },
          },
          {
            name: "CardChartInsurance",
            settings: {
              sidebar: true,
              showVerifiedChip: false,
              showCheckEligibilityButton: false,
              showEligibilitySummary: false,
              showCheckSameSimButton: false,
              showCheckSNFButton: false,
            },
          },
          { name: "CardChartClinical", settings: { sidebar: true } },
        ];
        rightColumnCards = [
          ...(hasViolations ? [{ name: "CardChartViolations" }] : []),
          { name: "CardChartPatientEquipment" },
          { name: "CardChartFeedback" },
          { name: "CardChartCommunications" },
          { name: "CardChartExternalIds" },
          { name: "CardChartNotes" },
        ];
        break;
    }

    const hasInsuranceDiscoveryAddressChanges =
      (chartContext.insuranceDiscoveryAddressChanges?.length ?? 0) > 0;

    const insuranceDiscoveryStepsWithCards = [
      {
        label: "Insurance Discovery",
        description: "Get insurance from pVerify",
        cards: [{ name: "CardChartInsuranceDiscovery" }],
      },
      {
        label: "Select Patient",
        description: "Choose a patient match",
        cards: [
          {
            name: "CardChartInsuranceDiscovery",
            settings: { step: "selectPatient" },
          },
        ],
      },
      {
        label: "Assign Coverage Type",
        description: "Assign as Primary, Secondary, or Tertiary",
        cards: [
          {
            name: "CardChartInsuranceDiscovery",
            settings: { step: "selectResult" },
          },
        ],
      },
      {
        label: "Review",
        description: "Confirm changes and return to chart",
        cards: [
          {
            name: "CardChartInsurance",
            settings: {
              showVerifiedChip: false,
              showCheckEligibilityButton: false,
              showEligibilitySummary: false,
              showCheckSameSimButton: false,
              showCheckSNFButton: false,
              showCreateVerificationNoteButton: false,
              allowDisablingFields: false,
              showEditButton: false,
            },
          },
          ...(hasInsuranceDiscoveryAddressChanges
            ? [
                {
                  name: "CardChartContact",
                  settings: { showEditButton: false },
                },
              ]
            : []),
        ],
      },
    ];

    /**
     * Renders one chart card and registers its anchor ref for sidebar navigation.
     * @param {{name: string, settings?: Object}} card
     * @param {number} cardIndex
     * @param {string} [columnName="default"]
     * @returns {JSX.Element}
     */
    const renderCard = (card, cardIndex, columnName = "default") => {
      const CardComponent = cardComponentMap[card.name];
      const cardAnchorKey = getCardAnchorKey(columnName, card.name, cardIndex);

      return (
        <MUIBox
          key={cardAnchorKey}
          ref={(element) => {
            if (element) {
              cardAnchorRefs.current[cardAnchorKey] = element;
            } else {
              delete cardAnchorRefs.current[cardAnchorKey];
            }
          }}
          sx={(theme) => ({
            scrollMarginTop: { xs: "130px", sm: "80px" },
            "& > .MuiCard-root": {
              transition: "background-color 260ms ease",
              backgroundColor:
                flashCardAnchorKey === cardAnchorKey
                  ? alpha(theme.palette.primary.main, 0.05)
                  : undefined,
            },
          })}
        >
          <CardComponent {...(card.settings || {})} />
        </MUIBox>
      );
    };

    /**
     * Renders the insurance discovery content.
     * @returns {JSX.Element} The insurance discovery content.
     */
    const renderInsuranceDiscoveryContent = () => {
      const patient =
        chartContext.chart?.patient[chartContext.patientId] || null;
      const order = chartContext.orderId
        ? chartContext.chart?.orderActivity?.[chartContext.orderId] || null
        : null;
      const isOrderApproved = order?.isApproved === true;
      const missingFields =
        InsuranceDiscovery.getInsurnaceDiscoveryMissingPatientFields(patient);

      const hasRequiredFields = missingFields.length === 0;
      return (
        <>
          <MUIBox sx={{ my: 6 }}>
            <HorizontalStepper
              steps={insuranceDiscoveryStepsWithCards.map(
                ({ label, description }) => ({ label, description }),
              )}
              activeStep={chartContext.insuranceDiscoveryActiveStep}
              onStepClick={chartContext.updateInsuranceDiscoveryActiveStep}
            />
          </MUIBox>
          <MUIBox>
            {(
              insuranceDiscoveryStepsWithCards[
                chartContext.insuranceDiscoveryActiveStep
              ]?.cards ?? []
            ).map((card, cardIndex) => renderCard(card, cardIndex, "single"))}
          </MUIBox>
          <MUIBox
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}
          >
            {chartContext.insuranceDiscoveryActiveStep > 0 && (
              <MUIButton
                variant="text"
                onClick={() => {
                  chartContext.updateInsuranceDiscoveryActiveStep(
                    Math.max(0, chartContext.insuranceDiscoveryActiveStep - 1),
                  );
                }}
              >
                Back
              </MUIButton>
            )}
            <MUIButton
              loading={chartContext.insuranceDiscoveryIsLoading}
              variant="contained"
              endIcon={<MUIArrowForwardIcon />}
              disabled={
                (chartContext.insuranceDiscoveryActiveStep === 0 &&
                  !hasRequiredFields) ||
                (chartContext.insuranceDiscoveryActiveStep === 1 &&
                  (chartContext.insuranceDiscoverySelectedPatientIndex ==
                    null ||
                    isOrderApproved)) ||
                (chartContext.insuranceDiscoveryActiveStep === 2 &&
                  (!Object.values(
                    chartContext.insuranceDiscoveryCoverageTypes || {},
                  ).some((v) => v === "primary" || v === "secondary") ||
                    isOrderApproved))
              }
              onClick={() => {
                if (chartContext.insuranceDiscoveryActiveStep === 0) {
                  chartContext.updateInsuranceDiscoveryIsLoading(true);
                  api
                    .send("POST", "/insuranceDiscovery", {
                      patientId: chartContext.patientId,
                    })
                    .then((res) => {
                      const apiResults =
                        InsuranceDiscovery.getInsuranceDiscovery(res);
                      chartContext.updateInsuranceDiscoveryApiResults(
                        apiResults,
                      );
                      chartContext.updateInsuranceDiscoveryIsLoading(false);
                      chartContext.updateInsuranceDiscoveryActiveStep(1);
                    })
                    .catch((err) => {
                      console.error(err);
                      chartContext.updateInsuranceDiscoveryApiResults({
                        status: "error",
                        detail: {
                          errorDescription: "Error running insurance discovery",
                        },
                      });
                      chartContext.updateInsuranceDiscoveryIsLoading(false);
                      chartContext.updateInsuranceDiscoveryActiveStep(1);
                    });
                } else if (chartContext.insuranceDiscoveryActiveStep === 3) {
                  if (!chartContext.hasPendingChanges) {
                    if (chartContext.orderId) {
                      history.push(
                        `/chart/${chartContext.patientId}/order/${chartContext.orderId}`,
                      );
                    } else {
                      history.push(`/chart/${chartContext.patientId}`);
                    }
                    chartContext.updateInsuranceDiscoveryActiveStep(0);
                  } else {
                    chartContext.handleSaveChart(null, () => {
                      if (chartContext.orderId) {
                        history.push(
                          `/chart/${chartContext.patientId}/order/${chartContext.orderId}`,
                        );
                      } else {
                        history.push(`/chart/${chartContext.patientId}`);
                      }
                      chartContext.updateInsuranceDiscoveryActiveStep(0);
                    });
                  }
                } else {
                  chartContext.updateInsuranceDiscoveryActiveStep(
                    chartContext.insuranceDiscoveryActiveStep + 1,
                  );
                }
              }}
            >
              {(() => {
                switch (chartContext.insuranceDiscoveryActiveStep) {
                  case 0:
                    return "Run Insurance Discovery";
                  case 3:
                    return "Save & Complete";
                  default:
                    return "Next";
                }
              })()}
            </MUIButton>
          </MUIBox>
        </>
      );
    };

    if (
      singleColumnCards.length > 0 ||
      chartContext.pageMode === "insuranceDiscovery"
    ) {
      return () => (
        <MUIBox
          sx={{
            maxWidth:
              chartContext.pageMode === "insuranceDiscovery" ? 1100 : 900,
            margin: "auto",
          }}
        >
          <MUIBox
            sx={{
              marginBottom: 2,
            }}
          >
            <CardChartHeader />
          </MUIBox>
          {chartContext.pageMode === "insuranceDiscovery" ? (
            renderInsuranceDiscoveryContent()
          ) : (
            <MUIBox>
              {singleColumnCards.map((card, cardIndex) =>
                renderCard(card, cardIndex, "single"),
              )}
            </MUIBox>
          )}
          <DialogManageSignature
            open={chartContext.showManageSignatureDialog}
            onClose={() => {
              chartContext.updateShowManageSignatureDialog(false);
            }}
            onConfirm={(signatureObject, initialObject) => {
              api
                .send("POST", "/signature", signatureObject)
                .then((signatureResponse) => {
                  mainContext.updateIndexed(
                    "signaturesIndexed",
                    signatureResponse,
                  );

                  if (initialObject && initialObject.data) {
                    return api
                      .send("POST", "/signature", initialObject)
                      .then((initialResponse) => {
                        mainContext.updateIndexed(
                          "signaturesIndexed",
                          initialResponse,
                        );
                        chartContext.updateShowManageSignatureDialog(false);
                      });
                  } else {
                    chartContext.updateShowManageSignatureDialog(false);
                  }
                })
                .catch((error) => {
                  console.error("Error creating signature", error);
                });
            }}
          />
        </MUIBox>
      );
    }

    return () => (
      <MUIStack
        sx={{
          height: { xs: "auto", sm: "calc(100vh - 85px)" },
          minHeight: 0,
          gap: 0.5,
        }}
      >
        {/* Banner Bar */}
        <MUIBox
          sx={{
            width: "100%",
          }}
        >
          <CardChartHeader />
        </MUIBox>
        {/* Columns */}
        <MUIStack
          direction={{ xs: "column", sm: "row" }}
          sx={{
            flex: 1,
            minHeight: 0,
            height: { xs: "auto", sm: "100%" },
            gap: 1.5,
          }}
        >
          <MUIBox
            sx={{
              flex: { xs: 1, sm: "0 0 30%" },
              minWidth: 0,
              minHeight: 0,
              overflowY: { xs: "visible", sm: "auto" },
            }}
            ref={(element) => {
              leftColumnScrollContainerRef.current = element;
            }}
          >
            <MUIStack sx={{ gap: 0 }}>
              {leftColumnCards.map((card, cardIndex) =>
                renderCard(card, cardIndex, "left"),
              )}
            </MUIStack>
          </MUIBox>
          <MUIBox
            sx={{
              flex: { xs: 1, sm: 1 },
              minWidth: 0,
              minHeight: 0,
              overflowY: { xs: "visible", sm: "auto" },
            }}
            ref={(element) => {
              rightColumnScrollContainerRef.current = element;
            }}
          >
            <MUIStack sx={{ gap: 0 }}>
              {rightColumnCards.map((card, cardIndex) =>
                renderCard(card, cardIndex, "right"),
              )}
            </MUIStack>
          </MUIBox>
          {mode === "viewOrderActivity" && (
            <ChartNavigationBar
              mode={mode}
              orderId={chartContext.orderId}
              rightColumnCards={rightColumnCards}
              cardComponentMap={cardComponentMap}
              getCardAnchorKey={getCardAnchorKey}
              cardAnchorRefs={cardAnchorRefs}
              leftColumnScrollContainerRef={leftColumnScrollContainerRef}
              rightColumnScrollContainerRef={rightColumnScrollContainerRef}
              scrollToCard={scrollToCard}
            />
          )}
        </MUIStack>
      </MUIStack>
    );
  };

  return (
    <LayerFunctional
      renderContent={renderContent()}
      loader={getLoader(location)}
    />
  );
}

/**
 * Constructs the loader configuration for fetching chart data.
 * @param {Object} location - React Router location object.
 * @returns {Object} Loader configuration object.
 */
function getLoader(location) {
  const parts = location.pathname.split("/");
  const patientId = parts[2] ? parseInt(parts[2]) : null;

  return {
    chart: {
      method: "POST",
      endpoint: "/chart/get",
      parameters: { PatientId: patientId },
    },
  };
}
